import { z } from 'zod';

export const judgeKindSchema = z.enum(['rubric', 'checklist']);
export type JudgeKind = z.infer<typeof judgeKindSchema>;

export const rubricScaleSchema = z.enum(['pass_fail', 'likert_5']);
export type RubricScale = z.infer<typeof rubricScaleSchema>;

export const qdrantContextSourceSchema = z.object({
	kind: z.literal('qdrant_chunks'),
	namespace: z.string(),
	topK: z.number().int().min(1).max(20),
});
export type QdrantContextSource = z.infer<typeof qdrantContextSourceSchema>;

export const contextSourceSchema = qdrantContextSourceSchema;
export type ContextSource = z.infer<typeof contextSourceSchema>;

export const judgeAppliesToSchema = z.object({
	modules: z.array(z.string()),
	agents: z.array(z.string()),
});
export type JudgeAppliesTo = z.infer<typeof judgeAppliesToSchema>;

const baseJudgeFields = {
	id: z.string(),
	name: z.string(),
	description: z.string(),
	enabled: z.boolean(),
	appliesTo: judgeAppliesToSchema,
	createdAt: z.string(),
	updatedAt: z.string(),
};

export const rubricJudgeSchema = z.object({
	...baseJudgeFields,
	kind: z.literal('rubric'),
	prompt: z.string(),
	scale: rubricScaleSchema,
	contextSources: z.array(contextSourceSchema).default([]),
});
export type RubricJudge = z.infer<typeof rubricJudgeSchema>;

export const checklistCriterionSchema = z.object({
	id: z.string(),
	label: z.string(),
	description: z.string(),
});
export type ChecklistCriterion = z.infer<typeof checklistCriterionSchema>;

export const checklistJudgeSchema = z.object({
	...baseJudgeFields,
	kind: z.literal('checklist'),
	criteria: z.array(checklistCriterionSchema).min(1),
});
export type ChecklistJudge = z.infer<typeof checklistJudgeSchema>;

export const judgeSchema = z.discriminatedUnion('kind', [rubricJudgeSchema, checklistJudgeSchema]);
export type Judge = z.infer<typeof judgeSchema>;

// ─── Verdicts / Evaluations ───────────────────────────────────────────────

export const rubricStatusSchema = z.enum(['passed', 'failed', '1', '2', '3', '4', '5']);
export type RubricStatus = z.infer<typeof rubricStatusSchema>;

export const rubricVerdictSchema = z.object({
	kind: z.literal('rubric'),
	judgeId: z.string(),
	judgeName: z.string(),
	scale: rubricScaleSchema,
	status: rubricStatusSchema,
	reasoning: z.string(),
	runAt: z.string(),
	durationMs: z.number().int().nonnegative(),
});
export type RubricVerdict = z.infer<typeof rubricVerdictSchema>;

export const checklistResultSchema = z.object({
	criterionId: z.string(),
	criterionLabel: z.string(),
	passed: z.boolean(),
	reasoning: z.string(),
});

export const checklistVerdictSchema = z.object({
	kind: z.literal('checklist'),
	judgeId: z.string(),
	judgeName: z.string(),
	results: z.array(checklistResultSchema),
	runAt: z.string(),
	durationMs: z.number().int().nonnegative(),
});
export type ChecklistVerdict = z.infer<typeof checklistVerdictSchema>;

export const externalEvaluationSourceSchema = z.enum(['intercom', 'human']);
export type ExternalEvaluationSource = z.infer<typeof externalEvaluationSourceSchema>;

export const externalEvaluationScaleSchema = z.enum(['1_to_5', 'thumbs', 'nps_0_10']);
export type ExternalEvaluationScale = z.infer<typeof externalEvaluationScaleSchema>;

export const externalEvaluationVerdictSchema = z.object({
	kind: z.literal('external_evaluation'),
	judgeId: z.string(),
	judgeName: z.string(),
	source: externalEvaluationSourceSchema,
	value: z.number().int(),
	scale: externalEvaluationScaleSchema,
	remark: z.string().nullable().optional(),
	metadata: z.record(z.unknown()).default({}),
	runAt: z.string(),
	durationMs: z.number().int().nonnegative(),
});
export type ExternalEvaluationVerdict = z.infer<typeof externalEvaluationVerdictSchema>;

export const evaluationSchema = z.discriminatedUnion('kind', [
	rubricVerdictSchema,
	checklistVerdictSchema,
	externalEvaluationVerdictSchema,
]);
export type Evaluation = z.infer<typeof evaluationSchema>;

// ─── Helpers ──────────────────────────────────────────────────────────────

export const isVerdictPassing = (e: Evaluation): boolean => {
	if (e.kind === 'rubric') {
		if (e.scale === 'pass_fail') return e.status === 'passed';
		const numeric = Number(e.status);

		return Number.isFinite(numeric) && numeric >= 4;
	}
	if (e.kind === 'external_evaluation') {
		if (e.scale === '1_to_5') return e.value >= 4;
		if (e.scale === 'thumbs') return e.value === 1;
		if (e.scale === 'nps_0_10') return e.value >= 9;
		return false;
	}
	return e.results.every((r) => r.passed);
};
